import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

/**
 * Базовый сервис для работы с API
 * Конвертировано из Angular MainService
 */
class ApiService {
    private axiosInstance: AxiosInstance;
    private baseURL = 'https://nct.gov.kz';

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: this.baseURL,
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }

    /**
     * Форматирование даты в YYYY-MM-DD
     */
    private formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Нормализация тела запроса (конвертация дат)
     */
    private normalizeBody(body: any): any {
        if (!body) {
            return {};
        }

        const normalized: any = {};
        for (const key in body) {
            if (body.hasOwnProperty(key)) {
                if (body[key] instanceof Date) {
                    normalized[key] = this.formatDate(body[key]);
                } else {
                    normalized[key] = body[key];
                }
            }
        }
        return normalized;
    }

    /**
     * GET запрос
     */
    async get<T = any>(uri: string, params?: any): Promise<T> {
        const normalizedParams = this.normalizeBody(params);
        const response = await this.axiosInstance.get<T>(uri, { params: normalizedParams });
        return response.data;
    }

    /**
     * POST запрос
     */
    async post<T = any>(uri: string, body?: any): Promise<T> {
        const normalizedBody = this.normalizeBody(body);
        const response = await this.axiosInstance.post<T>(uri, normalizedBody);
        return response.data;
    }

    /**
     * PUT запрос
     */
    async put<T = any>(uri: string, body?: any): Promise<T> {
        const normalizedBody = this.normalizeBody(body);
        const response = await this.axiosInstance.put<T>(uri, normalizedBody);
        return response.data;
    }

    /**
     * DELETE запрос
     */
    async delete<T = any>(uri: string, body?: any): Promise<T> {
        const normalizedBody = this.normalizeBody(body);
        const response = await this.axiosInstance.delete<T>(uri, { data: normalizedBody });
        return response.data;
    }

    /**
     * POST запрос с JWT токеном
     */
    async postWithJWT<T = any>(uri: string, body?: any): Promise<T> {
        const token = localStorage.getItem('jwtToken');
        const normalizedBody = this.normalizeBody(body);

        const config: AxiosRequestConfig = {
            headers: {
                'Authorization': `JWT ${token}`
            }
        };

        const response = await this.axiosInstance.post<T>(uri, normalizedBody, config);
        return response.data;
    }

    /**
     * POST запрос с отслеживанием прогресса (для загрузки файлов)
     */
    async postWithProgress<T = any>(
        uri: string,
        formData: FormData,
        onProgress?: (progress: number) => void
    ): Promise<T> {
        const config: AxiosRequestConfig = {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
                if (onProgress && progressEvent.total) {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(progress);
                }
            }
        };

        const response = await this.axiosInstance.post<T>(uri, formData, config);
        return response.data;
    }
}

export default new ApiService();
